import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { accountDeletionPolicy, deletionPolicyReady } from '../config/accountDeletion';
import { requestAccountDeletion, confirmAccountDeletion } from '../services/accountDeletionService';
import './AccountDeletion.css';

export default function AccountDeletion() {
  const [step, setStep] = useState('phone');
  const [phone, setPhone] = useState('');
  const [method, setMethod] = useState('phone');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [resendAt, setResendAt] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const requestId = useRef(null);
  const inFlight = useRef(false);
  const heading = useRef(null);
  const policy = accountDeletionPolicy;
  const ready = deletionPolicyReady(policy);
  const validIdentifier = method === 'phone' ? /^[0-9]{10}$/.test(phone) : /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  useEffect(() => {
    const previous = document.title;
    document.title = 'Delete your account | Click Events';
    return () => { document.title = previous; requestId.current = null; };
  }, []);

  useEffect(() => { heading.current?.focus(); }, [step]);
  useEffect(() => {
    const tick = () => setRemaining(Math.max(0, Math.ceil((resendAt - Date.now()) / 1000)));
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [resendAt]);

  async function run(action) {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError('');
    try { await action(); } catch (err) {
      if (err.response?.status === 429) {
        setResendAt(Date.now() + 30000);
        setRemaining(30);
        setError('Too many requests. Please try again later.');
      } else if (err.response?.status === 400 && step === 'otp') {
        setError('Invalid verification code. Check the code or request a new one.');
      } else if (step === 'otp') {
        setError('We could not confirm receipt of your deletion request. Contact support before retrying if the connection was interrupted.');
      } else {
        setError('We could not send a verification code. Please try again or contact support.');
      }
    } finally { inFlight.current = false; setBusy(false); }
  }

  function sendCode(event) {
    event.preventDefault();
    if (!validIdentifier || Date.now() < resendAt) return;
    run(async () => {
      requestId.current = await requestAccountDeletion(method === 'phone'
        ? { mobile_number: phone, country_code: '+91' }
        : { email: email.trim() });
      setConfirmed(false);
      setOtp('');
      setResendAt(Date.now() + 30000);
      setRemaining(30);
      setStep('otp');
    });
  }

  function reset() {
    requestId.current = null;
    setOtp(''); setConfirmed(false); setError(''); setStep('phone');
  }

  return (
    <main className="deletion-page">
      <div className="deletion-layout">
        <section className="deletion-intro" aria-labelledby="deletion-title">
          <p className="deletion-eyebrow">CLICK EVENTS · ACCOUNT & DATA</p>
          <h1 id="deletion-title">Your account.<br />Your choice.</h1>
          <p>Request permanent deletion of your Click Events account and associated personal data. You don’t need to log in or install the app.</p>
          <section className="deletion-policy" aria-label="Deletion and retention details">
            <h2>What happens to your data?</h2>
            <h3>Data deleted</h3>
            <p>{policy.deletedData || 'Contact support for the confirmed scope of account and associated data deletion.'}</p>
            <h3>Retained data and retention periods</h3>
            <p>{policy.retainedData || 'The retention details are not yet available on this page. Support can explain which records are retained, why, and for how long.'}</p>
            <h3>When deletion is completed</h3>
            <p>{policy.timeframe || 'Contact support for the confirmed processing timeframe.'}</p>
          </section>
          <p className="deletion-support">Need help or no longer have access to your registered phone or email? Email <a href={`mailto:${policy.supportEmail}?subject=Click%20Events%20account%20deletion`}>{policy.supportEmail}</a>.</p>
        </section>

        <section className="deletion-card" aria-labelledby="deletion-step-title" aria-busy={busy}>
          <p className="deletion-eyebrow">{step === 'success' ? 'REQUEST RECEIVED' : `STEP ${{ phone: 1, otp: 2 }[step]} OF 2`}</p>
          <h2 id="deletion-step-title" ref={heading} tabIndex="-1">{{ phone: 'Delete your account', otp: 'Verify and confirm deletion', success: 'Deletion request submitted' }[step]}</h2>
          {error && <p className="deletion-error" role="alert">{error}</p>}
          {!ready && <p className="deletion-notice">For details about deleted data, retention periods, and processing time, contact support before confirming deletion.</p>}
          {step === 'phone' && <form onSubmit={sendCode}>
            <p>Choose your registered phone number or email. If an account exists, we’ll send a one-time verification code.</p>
            <fieldset className="deletion-method" disabled={busy}>
              <legend>Verify using</legend>
              <label><input type="radio" name="deletion-method" checked={method === 'phone'} onChange={() => setMethod('phone')} /> Phone number</label>
              <label><input type="radio" name="deletion-method" checked={method === 'email'} onChange={() => setMethod('email')} /> Email</label>
            </fieldset>
            {method === 'phone' ? <>
              <label htmlFor="deletion-phone">Registered phone number</label>
              <div className="deletion-phone"><span>+91</span><input id="deletion-phone" type="tel" inputMode="numeric" autoComplete="tel-national" pattern="[0-9]{10}" maxLength={10} required value={phone} disabled={busy} onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))} aria-describedby="deletion-phone-hint" /></div>
              <p id="deletion-phone-hint" className="deletion-hint">Enter the 10-digit Indian mobile number used for Click Events.</p>
            </> : <>
              <label htmlFor="deletion-email">Registered email</label>
              <input id="deletion-email" type="email" autoComplete="email" required value={email} disabled={busy} onChange={(e) => setEmail(e.target.value)} />
            </>}
            <button disabled={busy || !validIdentifier || remaining > 0}>{busy ? 'Sending code…' : remaining > 0 ? `Try again in ${remaining}s` : 'Send verification code'}</button>
          </form>}
          {step === 'otp' && <form onSubmit={(event) => {
            event.preventDefault();
            if (!confirmed || !requestId.current || !/^[0-9]{6}$/.test(otp)) return;
            run(async () => {
              await confirmAccountDeletion(requestId.current, otp);
              requestId.current = null;
              setOtp(''); setPhone(''); setEmail(''); setStep('success');
            });
          }}>
            <p>If an account exists, a verification code has been sent to {method === 'phone' ? `+91 ${phone}` : email.trim()}.</p>
            <p>Entering the code and confirming below requests permanent deletion. This cannot be undone.</p>
            <label htmlFor="deletion-otp">6-digit verification code</label>
            <input id="deletion-otp" type="text" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} required value={otp} disabled={busy} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} />
            <label className="deletion-check"><input type="checkbox" required checked={confirmed} disabled={busy} onChange={(e) => setConfirmed(e.target.checked)} /><span>I understand and request permanent deletion of my Click Events account and associated data.</span></label>
            <button className="deletion-danger" disabled={busy || !confirmed || otp.length !== 6}>{busy ? 'Submitting…' : 'Permanently delete my account'}</button>
            <button className="deletion-secondary" type="button" disabled={busy || remaining > 0} onClick={sendCode}>{remaining > 0 ? `Resend code in ${remaining}s` : 'Resend code'}</button>
            <button className="deletion-secondary" type="button" disabled={busy} onClick={reset}>Change phone or email / Cancel</button>
          </form>}
          {step === 'success' && <div role="status"><p>Your account deletion request has been accepted.</p><p>{policy.timeframe}</p><p>The data and retention details on this page apply to your request. Contact support if you need help.</p><Link to="/">Back to home</Link></div>}
          {step !== 'success' && <p className="deletion-hint">Nothing is submitted for deletion until you enter your verification code and confirm.</p>}
        </section>
      </div>
    </main>
  );
}
